// Family Macro Meal Prep System — default plan content.
// Macros are practical estimates per serving: { cal, p, c, f } in grams.

export const macroTargets = [
  { person: 'Husband', calories: '2,400–2,600', protein: '160–180g', carbs: '230–260g', fat: '60–80g' },
  { person: 'Wife', calories: '1,400–1,600', protein: '110–130g', carbs: '110–140g', fat: '45–60g' },
  { person: 'Kids (each)', calories: 'To appetite', protein: '40–70g', carbs: 'To appetite', fat: 'To appetite' },
];

export const principles = [
  { title: 'Prioritize protein', text: 'Every meal = lean protein + produce + a smart carb.' },
  { title: 'Cook once', text: 'One protein, one carb, one veg prep serves multiple meals.' },
  { title: 'Adjust portions, not recipes', text: 'Same food — bigger carbs/protein for him, more veg for her, kids to appetite.' },
  { title: 'Cap prep time', text: 'Sunday ≤ 90 min, midweek ≤ 45 min. No complicated recipes.' },
];

export const breakfasts = [
  { name: 'Greek yogurt + berries + granola', cal: 320, p: 30, c: 38, f: 6 },
  { name: 'Egg & turkey sausage muffin cups (3)', cal: 300, p: 28, c: 6, f: 18 },
  { name: 'Protein overnight oats', cal: 380, p: 32, c: 45, f: 9 },
  { name: 'Cottage cheese + pineapple + almonds', cal: 290, p: 28, c: 20, f: 11 },
  { name: 'Protein smoothie (whey, banana, PB, milk)', cal: 360, p: 35, c: 34, f: 10 },
];

export const lunches = {
  sunday: {
    name: 'Sunday Batch — Chicken, Rice & Roasted Veg Bowls',
    serves: 8,
    ingredients: [
      '4 lb boneless chicken breast',
      '6 cups cooked rice (2 cups dry)',
      '2 heads broccoli + 3 bell peppers',
      '3 tbsp olive oil, garlic, paprika, salt & pepper',
    ],
    instructions: [
      'Season chicken; bake at 425°F for 22–25 min, then slice.',
      'Cook rice (rice cooker or pot) while chicken roasts.',
      'Toss veg in oil + seasoning; roast on a sheet pan 20 min.',
      'Build 8 bowls; refrigerate. Reheats all week.',
    ],
    macros: { cal: 520, p: 48, c: 55, f: 12 },
  },
  wednesday: {
    name: 'Wednesday Batch — Ground Turkey Taco Bowls',
    serves: 6,
    ingredients: [
      '2 lb ground turkey 93/7',
      '1 can black beans + 3 cups cooked brown rice',
      'Salsa, romaine, shredded cheese',
      'Taco seasoning (low sodium)',
    ],
    instructions: [
      'Brown turkey with taco seasoning, 10 min.',
      'Warm beans + rice.',
      'Assemble bowls with salsa, lettuce, a little cheese.',
    ],
    macros: { cal: 500, p: 42, c: 48, f: 14 },
  },
};

export const dinners = [
  {
    name: 'Sheet-Pan Salmon, Potatoes & Asparagus',
    ingredients: ['2 lb salmon', '2 lb baby potatoes', '2 bunches asparagus', 'Olive oil, lemon, garlic'],
    instructions: ['Roast potatoes at 425°F 15 min.', 'Add salmon + asparagus; roast 12–15 min.'],
    macros: { cal: 540, p: 44, c: 38, f: 22 },
    adjust: { husband: 'Extra potatoes + larger salmon portion', wife: 'Smaller potatoes, double asparagus', kids: 'Flake salmon, ketchup-friendly potatoes' },
  },
  {
    name: 'Chicken Fajita Bowls',
    ingredients: ['2.5 lb chicken', 'Peppers & onions', 'Rice', 'Fajita seasoning, salsa'],
    instructions: ['Sauté chicken + veg with seasoning.', 'Serve over rice with salsa.'],
    macros: { cal: 510, p: 46, c: 50, f: 12 },
    adjust: { husband: 'Double rice + extra chicken', wife: '½ rice, extra peppers', kids: 'Plain chicken + cheese quesadilla option' },
  },
  {
    name: 'Turkey & Veggie Chili',
    ingredients: ['2 lb ground turkey', 'Beans, tomatoes, peppers, onion', 'Chili spices'],
    instructions: ['Brown turkey; add veg, beans, tomatoes + spices.', 'Simmer 25 min.'],
    macros: { cal: 430, p: 40, c: 35, f: 12 },
    adjust: { husband: 'Add rice or cornbread', wife: 'Top with Greek yogurt, skip carbs', kids: 'Mild batch, serve with crackers' },
  },
  {
    name: 'Beef & Broccoli Stir-Fry',
    ingredients: ['2 lb lean sirloin', 'Broccoli', 'Rice', 'Low-sodium soy, garlic, ginger'],
    instructions: ['Sear beef; remove.', 'Stir-fry broccoli; combine with sauce; serve over rice.'],
    macros: { cal: 520, p: 45, c: 48, f: 16 },
    adjust: { husband: 'Extra rice + beef', wife: 'Cauliflower rice swap', kids: 'Less sauce, extra rice' },
  },
  {
    name: 'Baked Chicken Parmesan + Zucchini & Pasta',
    ingredients: ['2.5 lb chicken', 'Marinara, part-skim mozzarella', 'Whole-wheat pasta', 'Zucchini'],
    instructions: ['Bake breaded chicken 20 min.', 'Top with marinara + cheese; serve with pasta + zucchini.'],
    macros: { cal: 560, p: 48, c: 52, f: 16 },
    adjust: { husband: 'Full pasta portion', wife: 'More zucchini, ½ pasta', kids: 'Buttered pasta + chicken' },
  },
  {
    name: 'Shrimp & Veggie Fried Rice',
    ingredients: ['2 lb shrimp', 'Mixed veg', 'Rice (day-old)', 'Eggs, soy, sesame'],
    instructions: ['Scramble eggs; set aside.', 'Stir-fry shrimp + veg; add rice, eggs, soy.'],
    macros: { cal: 480, p: 42, c: 50, f: 12 },
    adjust: { husband: 'Extra shrimp + rice', wife: 'More veg, ½ rice', kids: 'Plain shrimp + rice' },
  },
  {
    name: 'Pork Tenderloin, Sweet Potato & Green Beans',
    ingredients: ['2 lb pork tenderloin', 'Sweet potatoes', 'Green beans', 'Olive oil, herbs'],
    instructions: ['Roast pork at 425°F 20–25 min.', 'Roast sweet potato + beans alongside.'],
    macros: { cal: 500, p: 46, c: 42, f: 14 },
    adjust: { husband: 'Extra sweet potato', wife: 'Smaller potato, extra beans', kids: 'Mashed sweet potato' },
  },
];

export const snacks = [
  { name: 'Nonfat Greek yogurt', cal: 100, p: 17 },
  { name: 'Cottage cheese (1 cup)', cal: 180, p: 24 },
  { name: 'Protein shake', cal: 130, p: 25 },
  { name: 'Hard-boiled eggs (2)', cal: 140, p: 12 },
  { name: 'Turkey roll-ups (4 slices)', cal: 120, p: 18 },
  { name: 'Tuna packet', cal: 90, p: 20 },
  { name: 'Beef jerky (1 oz)', cal: 80, p: 13 },
  { name: 'Edamame (1 cup)', cal: 190, p: 17 },
  { name: 'String cheese (2)', cal: 160, p: 14 },
  { name: 'Protein bar', cal: 210, p: 20 },
];

export const shoppingList = {
  Proteins: ['Chicken breast (6 lb)', 'Ground turkey 93/7 (4 lb)', 'Salmon (2 lb)', 'Shrimp (2 lb)', 'Lean sirloin (2 lb)', 'Pork tenderloin (2 lb)', 'Eggs (2 dozen)'],
  Produce: ['Broccoli (3)', 'Bell peppers (8)', 'Asparagus (2 bunches)', 'Zucchini (4)', 'Green beans', 'Onions', 'Romaine', 'Berries', 'Bananas', 'Sweet potatoes', 'Baby potatoes'],
  Carbs: ['White rice (5 lb)', 'Brown rice', 'Whole-wheat pasta', 'Oats', 'Black beans', 'Chili beans'],
  'Healthy Fats': ['Olive oil', 'Almonds', 'Peanut butter', 'Avocado'],
  Dairy: ['Nonfat Greek yogurt (large)', 'Cottage cheese', 'Part-skim mozzarella', 'String cheese', 'Milk'],
  Pantry: ['Marinara', 'Salsa', 'Low-sodium soy sauce', 'Taco/chili/fajita seasoning', 'Whey protein', 'Granola', 'Canned tuna', 'Jerky', 'Protein bars'],
};

export const cost = {
  weekly: 245,
  perMeal: 3.25,
  perServing: 2.70,
  reductions: [
    { pct: 10, tip: 'Buy chicken, rice & oats in bulk; choose store brands for dairy & pantry.' },
    { pct: 20, tip: 'Swap some salmon/beef nights for eggs, beans & turkey; use frozen vegetables.' },
    { pct: 30, tip: 'Anchor on chicken/turkey/eggs, plan deliberate leftovers, and cut pre-packaged snacks for DIY.' },
  ],
};

export const adherence = {
  scores: [
    { dim: 'Simplicity', score: 9 },
    { dim: 'Family friendliness', score: 8 },
    { dim: 'Protein quality', score: 9 },
    { dim: 'Cost efficiency', score: 8 },
    { dim: 'Meal prep efficiency', score: 9 },
    { dim: 'Sustainability', score: 8 },
  ],
  weaknesses: 'Salmon and lean beef raise cost and prep slightly; very picky kids may need a simple fallback (quesadilla/buttered pasta). Variety is strong but relies on weekend batch discipline — protect the 90-minute Sunday block.',
};

export const rotations = {
  breakfast: [
    'Week 1 — Greek yogurt parfaits',
    'Week 2 — Egg & turkey muffin cups',
    'Week 3 — Protein overnight oats',
    'Week 4 — Smoothies + cottage cheese',
  ],
  lunch: [
    'Week 1 — Chicken rice bowls / Turkey taco bowls',
    'Week 2 — Chicken fajita bowls / Tuna pasta salad',
    'Week 3 — Beef & broccoli bowls / Turkey chili',
    'Week 4 — Shrimp rice bowls / Chicken Caesar wraps',
  ],
  dinner: [
    'Salmon sheet-pan', 'Chicken fajita bowls', 'Turkey chili', 'Beef & broccoli',
    'Chicken parmesan', 'Shrimp fried rice', 'Pork tenderloin', 'Taco night',
    'Baked cod + veg', 'Chicken stir-fry', 'Turkey burgers + sweet potato fries', 'Spaghetti & turkey meatballs',
    'Sheet-pan chicken sausage', 'Beef chili', 'Honey-garlic salmon', 'BBQ chicken + slaw',
  ],
};

// Meal Prep Score (0-100): four equally-weighted components.
export const computeMealPrepScore = ({ proteinPct = 0, caloriesPct = 0, homeCookedDinners = 0, prepCompletionPct = 0 }) => {
  const homePct = Math.min(100, (homeCookedDinners / 7) * 100);
  const clamp = (n) => Math.max(0, Math.min(100, n));
  return Math.round((clamp(proteinPct) + clamp(caloriesPct) + homePct + clamp(prepCompletionPct)) / 4);
};

export const scoreCoaching = (score) => {
  if (score >= 90) return 'Dialed in. Keep protecting your Sunday prep block and progressive overload in training.';
  if (score >= 80) return 'Strong week. Tighten the lowest component next week to push past 90.';
  if (score >= 70) return 'Solid base. Pre-portion proteins and snacks to lift protein and home-cooked counts.';
  if (score >= 50) return 'Slipping — recommit to the 90-minute Sunday prep and grab-and-go breakfasts to cut decision fatigue.';
  return 'Reset week: do one batch protein + one batch carb on Sunday and aim for 5 home-cooked dinners.';
};
